// TEXT TRUNCATION UTILITY
class TextTruncator {
  constructor(options = {}) {
    this.options = {
      maxLength: options.maxLength || 200,
      readMoreText: options.readMoreText || 'Read more',
      readLessText: options.readLessText || 'Read less',
      truncateClass: options.truncateClass || 'js-truncate',
      expandedClass: options.expandedClass || 'is-expanded',
      animationDuration: options.animationDuration || 300
    };
    
    this.init();
  }

  init() {
    // Use event delegation for better performance
    document.addEventListener('click', (e) => {
      if (e.target.matches(`.${this.options.truncateClass}__toggle`)) {
        this.toggleText(e.target);
      }
    });

    // Initialize truncation after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.truncateAll());
    } else {
      this.truncateAll();
    }

    // Handle dynamic content loading (e.g., Shopify sections)
    document.addEventListener('shopify:section:load', (event) => {
      this.truncateAll(event.target);
    });
  }

  truncateAll(container = document) {
    const textBlocks = container.querySelectorAll(`.${this.options.truncateClass}`);
    
    textBlocks.forEach(block => {
      if (!block.dataset.initialized) {
        this.setupTextBlock(block);
      }
    });
  }

  setupTextBlock(block) {
    const text = block.textContent.trim();
    const originalText = text;
    
    if (text.length <= this.options.maxLength) {
      return;
    }

    // Create wrapper for the text
    const wrapper = document.createElement('div');
    wrapper.className = `${this.options.truncateClass}__wrapper`;
    
    // Create truncated text
    const truncatedText = document.createElement('div');
    truncatedText.className = `${this.options.truncateClass}__text`;
    truncatedText.textContent = text.substring(0, this.options.maxLength) + '...';
    
    // Create full text (hidden initially)
    const fullText = document.createElement('div');
    fullText.className = `${this.options.truncateClass}__full-text`;
    fullText.textContent = originalText;
    fullText.style.display = 'none';
    
    // Create toggle button
    const toggle = document.createElement('button');
    toggle.className = `${this.options.truncateClass}__toggle`;
    toggle.textContent = this.options.readMoreText;
    toggle.setAttribute('aria-expanded', 'false');
    
    // Assemble the structure
    wrapper.appendChild(truncatedText);
    wrapper.appendChild(fullText);
    wrapper.appendChild(toggle);
    
    // Clear and append new structure
    block.textContent = '';
    block.appendChild(wrapper);
    
    // Mark as initialized
    block.dataset.initialized = 'true';
  }

  toggleText(toggleButton) {
    const wrapper = toggleButton.closest(`.${this.options.truncateClass}__wrapper`);
    const truncatedText = wrapper.querySelector(`.${this.options.truncateClass}__text`);
    const fullText = wrapper.querySelector(`.${this.options.truncateClass}__full-text`);
    const isExpanded = wrapper.classList.contains(this.options.expandedClass);
    
    // Toggle classes and visibility
    wrapper.classList.toggle(this.options.expandedClass);
    truncatedText.style.display = isExpanded ? 'block' : 'none';
    fullText.style.display = isExpanded ? 'none' : 'block';
    
    // Update button text and aria state
    toggleButton.textContent = isExpanded ? this.options.readMoreText : this.options.readLessText;
    toggleButton.setAttribute('aria-expanded', !isExpanded);

    // Move the toggle button to the correct position
    if (isExpanded) {
      // Move button after truncated text
      truncatedText.after(toggleButton);
    } else {
      // Move button after full text
      fullText.after(toggleButton);
    }
  }
}

// INLINE TEXT TRUNCATION UTILITY (SMALLER VERSION)
class InlineTextTruncator {
  constructor(options = {}) {
    this.options = {
      maxLength: options.maxLength || 180,
      readMoreText: options.readMoreText || 'Read more',
      readLessText: options.readLessText || 'Read less',
      truncateClass: options.truncateClass || 'js-truncate-small',
      expandedClass: options.expandedClass || 'is-expanded',
      animationDuration: options.animationDuration || 300
    };
    
    this.init();
  }

  init() {
    // Use event delegation for better performance
    document.addEventListener('click', (e) => {
      if (e.target.matches(`.${this.options.truncateClass}__toggle`)) {
        this.toggleText(e.target);
      }
    });

    // Initialize truncation after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.truncateAll());
    } else {
      this.truncateAll();
    }

    // Handle dynamic content loading (e.g., Shopify sections)
    document.addEventListener('shopify:section:load', (event) => {
      this.truncateAll(event.target);
    });
  }

  truncateAll(container = document) {
    const textBlocks = container.querySelectorAll(`.${this.options.truncateClass}`);
    
    textBlocks.forEach(block => {
      if (!block.dataset.initialized) {
        this.setupTextBlock(block);
      }
    });
  }

  setupTextBlock(block) {
    const text = block.textContent.trim();
    const originalText = text;
    
    if (text.length <= this.options.maxLength) {
      return;
    }

    // Create wrapper for the text
    const wrapper = document.createElement('div');
    wrapper.className = `${this.options.truncateClass}__wrapper`;
    
    // Create truncated text
    const truncatedText = document.createElement('span');
    truncatedText.className = `${this.options.truncateClass}__text`;
    truncatedText.textContent = text.substring(0, this.options.maxLength) + '...';
    
    // Create full text (hidden initially)
    const fullText = document.createElement('span');
    fullText.className = `${this.options.truncateClass}__full-text`;
    fullText.textContent = originalText;
    fullText.style.display = 'none';
    
    // Create toggle button
    const toggle = document.createElement('button');
    toggle.className = `${this.options.truncateClass}__toggle`;
    toggle.textContent = this.options.readMoreText;
    toggle.setAttribute('aria-expanded', 'false');
    
    // Assemble the structure
    wrapper.appendChild(truncatedText);
    wrapper.appendChild(toggle);
    wrapper.appendChild(fullText);
    
    // Clear and append new structure
    block.textContent = '';
    block.appendChild(wrapper);
    
    // Mark as initialized
    block.dataset.initialized = 'true';
  }

  toggleText(toggleButton) {
    const wrapper = toggleButton.closest(`.${this.options.truncateClass}__wrapper`);
    const truncatedText = wrapper.querySelector(`.${this.options.truncateClass}__text`);
    const fullText = wrapper.querySelector(`.${this.options.truncateClass}__full-text`);
    const isExpanded = wrapper.classList.contains(this.options.expandedClass);
    
    // Toggle classes and visibility
    wrapper.classList.toggle(this.options.expandedClass);
    truncatedText.style.display = isExpanded ? 'inline' : 'none';
    fullText.style.display = isExpanded ? 'none' : 'inline';
    
    // Update button text and aria state
    toggleButton.textContent = isExpanded ? this.options.readMoreText : this.options.readLessText;
    toggleButton.setAttribute('aria-expanded', !isExpanded);

    // Move the toggle button to the correct position
    if (isExpanded) {
      // Move button after truncated text
      truncatedText.after(toggleButton);
    } else {
      // Move button after full text
      fullText.after(toggleButton);
    }
  }
}

// Initialize both truncators
const textTruncator = new TextTruncator();
const inlineTextTruncator = new InlineTextTruncator();

// Export for potential use in other scripts
window.TextTruncator = TextTruncator;
window.InlineTextTruncator = InlineTextTruncator;
